import { Clear, Search } from '@mui/icons-material';
import {
    ButtonBase,
    Chip,
    InputAdornment,
    SxProps,
    TextField,
    TextFieldProps,
    Typography,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import { ChangeEventHandler, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type SearchFieldProps = {
    setValue: (value: string) => void;
    value: string;
    chipLabel?: ReactNode;
    onChipDelete?: () => void;
    hideBorder?: boolean;
    resettable?: boolean;
    isActive?: boolean;
    sx?: SxProps;
} & TextFieldProps;

export const SearchField = ({
    value,
    setValue,
    onChipDelete,
    hideBorder,
    chipLabel,
    resettable = false,
    isActive = false,
    sx,
    ...props
}: SearchFieldProps) => {
    const { t } = useTranslation();
    const hideClearAction = value?.length === 0;
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isActive && resettable) {
            setValue('');
        }
    }, [isActive, setValue, resettable]);

    const onReset = () => {
        setValue('');
        inputRef.current?.focus();
    };

    const onDeleteChip = () => {
        if (onChipDelete) {
            onChipDelete();
        }
    };

    const onChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setValue(e.target.value);
    };

    return (
        <>
            <input style={{ display: 'none' }} type="text" autoComplete="off" />

            <TextField
                data-testid="search-bar"
                {...props}
                inputRef={inputRef}
                fullWidth
                size="small"
                value={value}
                onChange={onChange}
                variant="outlined"
                sx={{
                    ...sx,
                    ...(hideBorder
                        ? {
                              '& .MuiOutlinedInput-notchedOutline': {
                                  border: 'none',
                              },
                          }
                        : {}),
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment
                            position="start"
                            sx={{ height: '40px', mr: '10px' }}
                        >
                            <Search sx={{ width: '24px', height: '24px' }} />
                            {chipLabel && (
                                <Chip
                                    label={chipLabel}
                                    onDelete={onDeleteChip}
                                    sx={{ m: '0 4px' }}
                                />
                            )}
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment
                            position="end"
                            sx={{
                                display: hideClearAction ? 'none' : 'flex',
                            }}
                        >
                            <ButtonBase
                                aria-label={t('Clear')}
                                type="button"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                }}
                                onClick={onReset}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: ({ palette }) =>
                                        palette.primary.main,
                                    borderRadius: '4px',
                                    padding: '2px 4px',
                                    '&:hover': {
                                        color: ({ palette }) =>
                                            darken(palette.primary.main, 0.4),
                                    },
                                }}
                            >
                                <Clear sx={{ color: 'inherit' }} />
                                <Typography
                                    sx={{
                                        fontSize: '1.4rem',
                                        fontWeight: 600,
                                        lineHeight: '2.4rem',
                                        color: 'inherit',
                                    }}
                                >
                                    {t('Clear')}
                                </Typography>
                            </ButtonBase>
                        </InputAdornment>
                    ),
                    sx: { height: '40px' },
                }}
            />
        </>
    );
};
